import Dialog from 'components/Dialog';
import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {usePostApi} from 'common/hooks/usePostApi';
import api from 'api';
import history from 'redux/history';
import {packId} from 'common/diplomatIdUtil';
import {IPartner, Partner, PartnerType} from 'types/partners';
import actions from 'features/layout/redux/actions';
import FormikTextField from 'components/inputs/FormikTextField';
import {useTranslation} from 'react-i18next';
import FormikRadioGroup from 'components/inputs/FormikRadioGroup';
import {DiplomatObjectType} from 'types/DiplomatObjectType';
import {diplomatObjectSchema} from 'types/diplomat-object';
import {IDiplomatObject} from 'types/DiplomatObject';
import {FormFooterButtons} from 'components/buttons/FormFooterButtons';
import {layoutSelectors} from 'features/layout/redux';
import {useModal} from 'common/hooks/useModal';
import {ConfirmationDialog} from 'components/dialogs/ConfirmationDialog';
import {INav} from 'features/layout/redux/helpers/FoldersHelper';
import {castToSend} from 'common/yup/castToSend';
import {localTransportSchema} from 'types/transport/Transport';
import {useEnsurePartnerFolderId} from 'common/hooks/useEnsurePartnerFolderId';
import Formik, {FormOnSubmitHelpers} from 'components/hook-form/Formik';

type CreatePartnerProps = {
   open?: boolean;
   header: string;
   data?: INav;
   objectType?: 'EXTERNAL_PARTNER' | 'INTERNAL_PARTNER';
   onClose: () => void;
};

const CreatePartnerDialog = ({
   open = true,
   header,
   data,
   objectType,
   onClose,
}: CreatePartnerProps) => {
   const {t} = useTranslation();
   const dispatch = useDispatch();
   const onSuccess = (data: IPartner) => {
      dispatch(actions.loadTreeNav());
      onClose();
      history.push(`/${packId(data.objectType, data.id)}`);
   };
   const {request} = usePostApi<IPartner>(api.partners, {onSuccess, onFailure: onClose});
   const [formValues, setFormValues] = React.useState<IDiplomatObject>();
   const ensureFolderId = useEnsurePartnerFolderId();

   const isDataFolder = data?.objectType === DiplomatObjectType.enum.FOLDER;

   const _handleSubmit = (_values?: IDiplomatObject) => {
      const values = _values || formValues;

      const _objectType = objectType || values?.objectType;
      const type =
         _objectType === DiplomatObjectType.enum.INTERNAL_PARTNER
            ? PartnerType.enum.INTERNAL
            : PartnerType.enum.EXTERNAL;

      const folderId =
         ensureFolderId(isDataFolder ? data?.id : data?.folderId, _objectType) || null;
      // console.debug('_handleSubmit', objectType, values?.objectType, type, values, data);
      const newPartner = {
         ...data,
         ...values,
         type,
         folderId,
         transport: ((data as unknown) as IPartner)?.transport || localTransportSchema.cast({}),
      };
      request(
         JSON.stringify({
            partner: isDataFolder
               ? newPartner
               : castToSend(Partner as any, newPartner, {stripUnknown: true}),
            overwrite: true,
            folderId,
         })
      );
   };

   const handleSubmitRef = React.useRef(_handleSubmit);
   handleSubmitRef.current = _handleSubmit;

   const {open: alertOpened, handleOpen: openAlert, handleClose: closeAlert} = useModal();
   const partners = useSelector(layoutSelectors.partnersSelector);

   const helpersRef = React.useRef<FormOnSubmitHelpers<IDiplomatObject> | null>(null);

   const validateAndSave = React.useCallback(
      (values: IDiplomatObject, helpers: FormOnSubmitHelpers<IDiplomatObject>) => {
         setFormValues(values);
         const {name} = values;
         const partner =
            partners.trustedPartners.find((obj: IDiplomatObject) => obj.name === name) ||
            partners.publicPartners.find((obj: IDiplomatObject) => obj.name === name);
         if (partner) {
            if (partner.objectType !== objectType) {
               helpers.setErrors({
                  name: t('Diplomat already contains a {{type}} partner named {{name}}', {
                     type: DiplomatObjectType[objectType!],
                     name,
                  }),
               });
               helpers.setSubmitting(false);
            } else {
               // Object.assign(helpersRef.current, helpers);
               helpersRef.current = helpers;
               openAlert();
            }
            return;
         }

         return handleSubmitRef.current(values);
      },
      [setFormValues, partners, openAlert, handleSubmitRef, objectType, t]
   );

   const onCloseAlert = React.useCallback(() => {
      helpersRef.current?.setSubmitting(false);
      closeAlert();
   }, [helpersRef, closeAlert]);
   return (
      <Dialog maxWidth="sm" header={header} open={open} close={onClose}>
         <Formik
            initialValues={diplomatObjectSchema.cast({
               name: '',
               objectType: objectType || DiplomatObjectType.enum.EXTERNAL_PARTNER,
               anonymous: false,
            })}
            onSubmit={validateAndSave}
         >
            <>
               <FormikTextField autoFocus label={t('New Partner Name:')} name={'name'} />
               {!objectType ? (
                  <FormikRadioGroup
                     row
                     label={t('Partner Type:')}
                     name={'objectType'}
                     options={[
                        {label: t('Public'), value: DiplomatObjectType.enum.EXTERNAL_PARTNER},
                        {label: t('Trusted'), value: DiplomatObjectType.enum.INTERNAL_PARTNER},
                     ]}
                  />
               ) : null}
               <FormFooterButtons cancel={onClose} />
            </>
         </Formik>
         <ConfirmationDialog
            open={alertOpened}
            content={t(
               'Diplomat already contains a partner with the name. \n Do you want to override it?'
            )}
            onOk={() => handleSubmitRef.current()}
            onCancel={onCloseAlert}
         />
      </Dialog>
   );
};

export default CreatePartnerDialog;
